import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Assessment,
  Business,
  Warning,
  CheckCircle,
  Pending,
  ExpandMore,
  ChevronRight,
  BarChart,
  Close
} from '@mui/icons-material';
import API_CONFIG from '../config/api';
import { apiFetch } from '../api/client';
import { withUnidade } from '../utils/storageByUnit';
import EvolucaoNotasFiscaisChart from '../components/contratos/EvolucaoNotasFiscaisChart';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import { withSetorScope } from '../utils/dataIsolation';
import {
  MESES_COMPETENCIA,
  normalizarCompetencia,
  extrairAnoCompetencia as extrairAnoCompUtil,
  extrairMesIndexCompetencia,
} from '../utils/competenciaUtils';

// Plugin para exibir valor líquido acima de cada barra no gráfico de gastos mensais
const valorLiquidoLabelsPlugin = {
  id: 'valorLiquidoLabels',
  afterDatasetsDraw(chart, _args, opts) {
    const valores = opts?.valorLiquidoPorBarra;
    if (!valores || !Array.isArray(valores) || valores.length === 0) return;
    const { ctx, data, scales: { x, y } } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data.length) return;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.font = 'bold 11px sans-serif';
    ctx.fillStyle = '#333';
    meta.data.forEach((bar, i) => {
      const v = parseFloat(valores[i]) || 0;
      if (v <= 0) return;
      const xPos = bar.x;
      const yPos = y.getPixelForValue(v) - 4;
      if (yPos < chart.chartArea.top) return;
      const label = 'R$ ' + (v / 1000).toFixed(1) + 'k';
      ctx.fillText(label, xPos, yPos);
    });
    ctx.restore();
  }
};

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  valorLiquidoLabelsPlugin
);

const BACKEND_URL = API_CONFIG.BACKEND_URL;

export default function DashboardFinanceiroPage() {
  const { unidadeSelecionada, user, rbacRole, fiscalSetor } = useAuth();
  const scopeOpts = { user, rbacRole, fiscalSetor };
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState({
    resumoGeral: {},
    porSetor: [],
    porEmpresa: [],
    avaliacoes: [],
    processosAtraso: []
  });
  const [error, setError] = useState(null);
  
  // Estados para o modal de gráfico
  const [modalGraficoOpen, setModalGraficoOpen] = useState(false);
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [loadingGrafico, setLoadingGrafico] = useState(false);
  const [totaisGrafico, setTotaisGrafico] = useState(null); // Totais absolutos do ano
  
  // Estados para média móvel e análise crítica
  const [mostrarMediaMovel, setMostrarMediaMovel] = useState(false);
  const [mediaMovelCustomizada, setMediaMovelCustomizada] = useState(null); // Valor customizado pelo usuário
  const [modalMediaMovelOpen, setModalMediaMovelOpen] = useState(false);
  const [valorMediaMovelInput, setValorMediaMovelInput] = useState('');
  const [modalAnaliseCriticaOpen, setModalAnaliseCriticaOpen] = useState(false);
  const [mesSelecionadoAnalise, setMesSelecionadoAnalise] = useState('');
  const [textoAnaliseCritica, setTextoAnaliseCritica] = useState('');
  const [analisesCriticas, setAnalisesCriticas] = useState({});
  
  // Estados para filtros de ano
  const [anoDashboardGeral, setAnoDashboardGeral] = useState(new Date().getFullYear().toString()); // 2025 ou 2026 - Filtro global para todos os blocos
  const [anoResumoFinanceiro, setAnoResumoFinanceiro] = useState(new Date().getFullYear().toString()); // 2025 ou 2026
  const [anoGrafico, setAnoGrafico] = useState('2025'); // 2025 ou 2026 - Iniciar com 2025
  const [anoGraficoPrestador, setAnoGraficoPrestador] = useState(new Date().getFullYear().toString()); // Para gráfico por prestador
  const [prestadorSelecionadoGrafico, setPrestadorSelecionadoGrafico] = useState('');
  const [modalGraficoPrestadorOpen, setModalGraficoPrestadorOpen] = useState(false);
  const [dadosGraficoPrestador, setDadosGraficoPrestador] = useState([]);
  const [loadingGraficoPrestador, setLoadingGraficoPrestador] = useState(false);
  
  // Armazenar dados brutos para filtros
  const [notasBrutas, setNotasBrutas] = useState([]);
  const [contratosBrutos, setContratosBrutos] = useState([]);

  // Evolução (12 meses) para o gráfico no dashboard financeiro
  const [evolucaoNotas, setEvolucaoNotas] = useState(null);
  const [loadingEvolucaoNotas, setLoadingEvolucaoNotas] = useState(true);
  const [anoGraficoNotas, setAnoGraficoNotas] = useState(new Date().getFullYear());

  // Buscar dados do dashboard
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Adicionar filtro de unidade nas URLs
      let urlNotas = unidadeSelecionada 
        ? `${BACKEND_URL}/api/notas-fiscais?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/notas-fiscais`;
      urlNotas = withSetorScope(urlNotas, scopeOpts);
      
      let urlContratos = unidadeSelecionada 
        ? `${BACKEND_URL}/api/prestadores?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/prestadores`;
      urlContratos = withSetorScope(urlContratos, scopeOpts);

      // Buscar todas as notas fiscais
      const resNotas = await apiFetch(urlNotas);
      if (!resNotas.ok) throw new Error('Erro ao buscar notas fiscais');
      const notas = await resNotas.json();

      const resContratos = await apiFetch(urlContratos);
      if (!resContratos.ok) throw new Error('Erro ao buscar contratos');
      const contratos = await resContratos.json();

      const resAvaliacoes = await apiFetch(withSetorScope(withUnidade('/api/avaliacoes-mensais', unidadeSelecionada), scopeOpts));
      const avaliacoes = resAvaliacoes.ok ? await resAvaliacoes.json() : [];

      // Processar dados (sem filtro de ano inicial - será aplicado no resumo)
      const dadosProcessados = processarDados(notas, contratos, avaliacoes);
      setDados(dadosProcessados);
      
      // Armazenar notas e contratos brutos para filtros
      setNotasBrutas(notas);
      setContratosBrutos(contratos);

    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada, user, rbacRole, fiscalSetor]);

  // Buscar evolução (12 meses, só notas fiscais) para o gráfico da aba Notas Fiscais
  useEffect(() => {
    let cancelled = false;
    setLoadingEvolucaoNotas(true);
    const params = new URLSearchParams();
    if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
    params.set('ano', String(anoGraficoNotas));
    params.set('meses', '12');
    params.set('apenasNotas', '1');
    const qs = `?${params.toString()}`;
    apiFetch(withSetorScope(`${BACKEND_URL}/api/dashboard-contratos/evolucao${qs}`, scopeOpts))
      .then(async (res) => {
        if (cancelled) return null;
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `Erro evolução (${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setEvolucaoNotas(data && Array.isArray(data.meses) ? data : { meses: [] });
      })
      .catch((e) => {
        console.error('[DashboardFinanceiro] evolução:', e);
        if (!cancelled) setEvolucaoNotas({ meses: [] });
      })
      .finally(() => {
        if (!cancelled) setLoadingEvolucaoNotas(false);
      });
    return () => { cancelled = true; };
  }, [unidadeSelecionada?.codigo, anoGraficoNotas, user, rbacRole, fiscalSetor]);

  // Função auxiliar para extrair ano da competência (aceita Julho/2026 e 07/2026)
  const extrairAnoCompetencia = (competencia) => extrairAnoCompUtil(competencia);

  const MESES_NOME = MESES_COMPETENCIA;

  /** Calcula dados do gráfico a partir das notas/contratos (mesma fonte do relatório mensal). */
  const computeDadosGraficoFromNotas = useCallback((ano) => {
    if (!notasBrutas || notasBrutas.length === 0) return { meses: [], totais: null };
    const anoStr = String(ano || anoGrafico || '2025');
    const notasAno = notasBrutas.filter((n) => extrairAnoCompetencia(n.competencia || n.competencia_mes) === anoStr);
    const byMes = {};
    for (let i = 1; i <= 12; i++) {
      byMes[i] = {
        mes: i,
        nome: `${MESES_NOME[i - 1]}/${anoStr}`,
        totalBruto: 0,
        totalLiquido: 0,
        terceirizada: 0,
        eventual: 0,
        corporativo: 0,
        medicos: 0,
        totalNotas: 0
      };
    }
    notasAno.forEach((nota) => {
      const compRaw = (nota.competencia || nota.competencia_mes || '').toString().trim();
      const comp = normalizarCompetencia(compRaw) || compRaw;
      const mes = extrairMesIndexCompetencia(comp);
      if (!mes || mes < 1 || mes > 12) return;
      const contrato = (contratosBrutos || []).find((c) => c.numeroContrato === nota.numero_contrato);
      const vBruto = parseFloat(nota.valor_bruto) || 0;
      const vLiq = parseFloat(nota.valor_liquido) || 0;
      const is = (v) => v === 1 || v === true || v === '1' || v === 'true';
      byMes[mes].totalBruto += vBruto;
      byMes[mes].totalLiquido += vLiq;
      byMes[mes].totalNotas += 1;
      if (contrato) {
        if (is(contrato.terceirizada)) byMes[mes].terceirizada += vLiq;
        else if (is(contrato.eventual)) byMes[mes].eventual += vLiq;
        else if (is(contrato.corporativo)) byMes[mes].corporativo += vLiq;
        else if (is(contrato.servicoMedico)) byMes[mes].medicos += vLiq;
      }
    });
    const meses = Object.values(byMes);
    const totalAbsolutoBruto = meses.reduce((s, m) => s + m.totalBruto, 0);
    const totalAbsolutoLiquido = meses.reduce((s, m) => s + m.totalLiquido, 0);
    const mesesComDados = meses.filter((m) => m.totalLiquido > 0).length || 12;
    const totalNotasAno = meses.reduce((s, m) => s + m.totalNotas, 0);
    const totais = {
      totalAbsolutoBruto,
      totalAbsolutoLiquido,
      valorMedioAnualBruto: totalNotasAno > 0 ? totalAbsolutoBruto / totalNotasAno : 0,
      valorMedioAnualLiquido: totalNotasAno > 0 ? totalAbsolutoLiquido / totalNotasAno : 0,
      totalNotasAno,
      mediaMensalBruta: totalAbsolutoBruto / mesesComDados,
      mediaMensalLiquida: totalAbsolutoLiquido / mesesComDados
    };
    return { meses, totais };
  }, [notasBrutas, contratosBrutos, anoGrafico]);

  // Processar dados para o dashboard
  const processarDados = (notas, contratos, avaliacoes, anoFiltro = null) => {
    // Filtrar notas por ano se anoFiltro for especificado
    let notasFiltradas = notas;
    if (anoFiltro) {
      notasFiltradas = notas.filter(nota => {
        const anoCompetencia = extrairAnoCompetencia(nota.competencia);
        return anoCompetencia === anoFiltro;
      });
    }

    // Resumo geral
    const totalProcessos = notasFiltradas.length;
    const processosPagos = notasFiltradas.filter(n => n.status === 'pago').length;
    const processosPendentes = notasFiltradas.filter(n => n.status === 'pendente_avaliacao' || n.status === 'pendente_pagamento').length;
    const processosAtraso = notasFiltradas.filter(n => {
      if (!n.data_vencimento) return false;
      const vencimento = new Date(n.data_vencimento);
      const hoje = new Date();
      return vencimento < hoje && n.status !== 'pago';
    }).length;

    const valorTotalBruto = notasFiltradas.reduce((sum, nota) => sum + (parseFloat(nota.valor_bruto) || 0), 0);
    const valorTotalLiquido = notasFiltradas.reduce((sum, nota) => sum + (parseFloat(nota.valor_liquido) || 0), 0);
    const valorMedio = totalProcessos > 0 ? valorTotalLiquido / totalProcessos : 0;

    // Agrupar por setor
    const porSetor = {};
    contratos.forEach(contrato => {
      if (!porSetor[contrato.setor]) {
        porSetor[contrato.setor] = {
          setor: contrato.setor,
          contratos: 0,
          notas: 0,
          valorBruto: 0,
          valorLiquido: 0,
          processosPagos: 0,
          processosPendentes: 0,
          processosAtraso: 0
        };
      }
      porSetor[contrato.setor].contratos++;
    });

    // Agrupar por empresa
    const porEmpresa = {};
    contratos.forEach(contrato => {
      if (!porEmpresa[contrato.empresa]) {
        porEmpresa[contrato.empresa] = {
          empresa: contrato.empresa,
          setor: contrato.setor,
          contratos: 0,
          notas: 0,
          valorBruto: 0,
          valorLiquido: 0,
          processosPagos: 0,
          processosPendentes: 0,
          processosAtraso: 0,
          avaliacaoMedia: 0
        };
      }
      porEmpresa[contrato.empresa].contratos++;
    });

    // Processar notas por setor e empresa
    notasFiltradas.forEach(nota => {
      const contrato = contratos.find(c => c.numeroContrato === nota.numero_contrato);
      if (contrato) {
        // Por setor
        if (porSetor[contrato.setor]) {
          porSetor[contrato.setor].notas++;
          porSetor[contrato.setor].valorBruto += parseFloat(nota.valor_bruto) || 0;
          porSetor[contrato.setor].valorLiquido += parseFloat(nota.valor_liquido) || 0;
          if (nota.status === 'pago') porSetor[contrato.setor].processosPagos++;
          if (nota.status === 'pendente_avaliacao' || nota.status === 'pendente_pagamento') porSetor[contrato.setor].processosPendentes++;
          if (new Date(nota.data_vencimento) < new Date() && nota.status !== 'pago') porSetor[contrato.setor].processosAtraso++;
        }

        // Por empresa
        if (porEmpresa[contrato.empresa]) {
          porEmpresa[contrato.empresa].notas++;
          porEmpresa[contrato.empresa].valorBruto += parseFloat(nota.valor_bruto) || 0;
          porEmpresa[contrato.empresa].valorLiquido += parseFloat(nota.valor_liquido) || 0;
          if (nota.status === 'pago') porEmpresa[contrato.empresa].processosPagos++;
          if (nota.status === 'pendente_avaliacao' || nota.status === 'pendente_pagamento') porEmpresa[contrato.empresa].processosPendentes++;
          if (new Date(nota.data_vencimento) < new Date() && nota.status !== 'pago') porEmpresa[contrato.empresa].processosAtraso++;
        }
      }
    });

    // Calcular avaliações médias por empresa
    Object.keys(porEmpresa).forEach(empresa => {
      const avaliacoesEmpresa = avaliacoes.filter(a => a.empresa === empresa);
      if (avaliacoesEmpresa.length > 0) {
        const somaAvaliacoes = avaliacoesEmpresa.reduce((sum, av) => sum + (parseFloat(av.nota_final) || 0), 0);
        porEmpresa[empresa].avaliacaoMedia = somaAvaliacoes / avaliacoesEmpresa.length;
      }
    });

    return {
      resumoGeral: {
        totalProcessos,
        processosPagos,
        processosPendentes,
        processosAtraso,
        valorTotalBruto,
        valorTotalLiquido,
        valorMedio
      },
      porSetor: Object.values(porSetor),
      porEmpresa: Object.values(porEmpresa),
      avaliacoes: avaliacoes,
      processosAtraso: notasFiltradas.filter(n => {
        if (!n.data_vencimento) return false;
        const vencimento = new Date(n.data_vencimento);
        const hoje = new Date();
        return vencimento < hoje && n.status !== 'pago';
      })
    };
  };

  // Função para buscar dados mensais para o gráfico
  const fetchDadosMensais = async (ano = null) => {
    try {
      setLoadingGrafico(true);
      console.log('📊 Buscando dados mensais...', ano ? `para o ano ${ano}` : '');
      
      // Adicionar filtro de unidade e ano na URL
      let url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/dashboard/grafico-tipos-servico?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/dashboard/grafico-tipos-servico`;
      
      if (ano) {
        url += unidadeSelecionada ? '&' : '?';
        url += `ano=${ano}`;
      }
      
      const response = await apiFetch(url);
      console.log('📊 Response status:', response.status);
      console.log('📊 Response ok:', response.ok);
      
      if (!response.ok) {
        console.error('❌ Erro na resposta:', response.status, response.statusText);
        throw new Error('Erro ao buscar dados mensais');
      }
      
      const responseData = await response.json();
      console.log('📊 Dados recebidos do backend:', responseData);
      console.log('📊 Tipo de dados:', typeof responseData);
      console.log('📊 É array?', Array.isArray(responseData));
      console.log('📊 Tem meses?', responseData.meses ? 'Sim' : 'Não');
      
      // Verificar se a resposta é um objeto com meses e totais, ou array direto
      let dadosFiltrados = [];
      let totais = null;
      
      if (responseData && responseData.meses && Array.isArray(responseData.meses)) {
        // Nova estrutura: { meses: [...], totais: {...} }
        dadosFiltrados = responseData.meses;
        totais = responseData.totais;
        console.log('✅ Usando nova estrutura (meses + totais)');
      } else if (Array.isArray(responseData)) {
        // Estrutura antiga: array direto
        dadosFiltrados = responseData;
        console.log('✅ Usando estrutura antiga (array direto)');
      } else {
        console.error('❌ Estrutura de dados desconhecida:', responseData);
        dadosFiltrados = [];
      }
      
      // Armazenar dados e totais
      setDadosGrafico(dadosFiltrados);
      setTotaisGrafico(totais);
      
      console.log('📊 Estado dadosGrafico atualizado:', dadosFiltrados.length, 'meses');
      if (totais) {
        console.log('💰 Totais recebidos:', totais);
      } else {
        console.log('⚠️ Nenhum total recebido do backend');
      }
    } catch (error) {
      console.error('❌ Erro ao buscar dados mensais:', error);
      setDadosGrafico([]);
    } finally {
      setLoadingGrafico(false);
    }
  };
  
  // Função para buscar dados mensais por prestador
  const fetchDadosMensaisPrestador = async (numeroContrato, ano) => {
    try {
      setLoadingGraficoPrestador(true);
      
      const url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/notas-fiscais/${encodeURIComponent(numeroContrato)}?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}&ano=${ano}`
        : `${BACKEND_URL}/api/notas-fiscais/${encodeURIComponent(numeroContrato)}?ano=${ano}`;
      
      const response = await apiFetch(url);
      if (!response.ok) throw new Error('Erro ao buscar notas fiscais do prestador');
      
      const notas = await response.json();
      
      // Agrupar por mês
      const dadosPorMes = {};
      notas.forEach(nota => {
        const anoCompetencia = extrairAnoCompetencia(nota.competencia);
        if (anoCompetencia === ano) {
          // Extrair mês da competência (ex: "Janeiro/2025" -> "Janeiro")
          const mes = nota.competencia?.split('/')[0];
          if (mes) {
            if (!dadosPorMes[mes]) {
              dadosPorMes[mes] = {
                mes: mes,
                valorBruto: 0,
                valorLiquido: 0,
                quantidade: 0
              };
            }
            dadosPorMes[mes].valorBruto += parseFloat(nota.valor_bruto) || 0;
            dadosPorMes[mes].valorLiquido += parseFloat(nota.valor_liquido) || 0;
            dadosPorMes[mes].quantidade += 1;
          }
        }
      });
      
      // Converter para array e ordenar por mês
      const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                     'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const dadosOrdenados = meses
        .filter(mes => dadosPorMes[mes])
        .map(mes => ({
          ...dadosPorMes[mes],
          nome: `${mes}/${ano}`
        }));
      
      setDadosGraficoPrestador(dadosOrdenados);
    } catch (error) {
      console.error('❌ Erro ao buscar dados do prestador:', error);
      setDadosGraficoPrestador([]);
    } finally {
      setLoadingGraficoPrestador(false);
    }
  };

  // Função para abrir modal de gráfico (usa relatório mensal quando há notas carregadas)
  const handleAbrirGrafico = () => {
    setModalGraficoOpen(true);
    const anoParaBuscar = anoGrafico || '2025';
    if (anoGrafico !== anoParaBuscar) {
      setAnoGrafico(anoParaBuscar);
    }
    if (notasBrutas && notasBrutas.length > 0) {
      const { meses, totais } = computeDadosGraficoFromNotas(anoParaBuscar);
      setDadosGrafico(meses);
      setTotaisGrafico(totais);
      setLoadingGrafico(false);
    } else {
      fetchDadosMensais(anoParaBuscar);
    }
    fetchAnalisesCriticas();
  };
  
  // Função para abrir modal de gráfico por prestador
  const handleAbrirGraficoPrestador = () => {
    setModalGraficoPrestadorOpen(true);
    if (prestadorSelecionadoGrafico && anoGraficoPrestador) {
      fetchDadosMensaisPrestador(prestadorSelecionadoGrafico, anoGraficoPrestador);
    }
  };
  
  // Calcular resumo financeiro filtrado por ano
  const calcularResumoFinanceiroPorAno = (ano) => {
    if (!notasBrutas || notasBrutas.length === 0) {
      return {
        valorTotalBruto: 0,
        valorTotalLiquido: 0,
        valorMedio: 0,
        totalNotas: 0
      };
    }
    
    const notasAno = notasBrutas.filter(nota => {
      const anoCompetencia = extrairAnoCompetencia(nota.competencia);
      return anoCompetencia === ano;
    });
    
    const valorTotalBruto = notasAno.reduce((sum, nota) => sum + (parseFloat(nota.valor_bruto) || 0), 0);
    const valorTotalLiquido = notasAno.reduce((sum, nota) => sum + (parseFloat(nota.valor_liquido) || 0), 0);
    const totalNotas = notasAno.length;
    const valorMedio = totalNotas > 0 ? valorTotalLiquido / totalNotas : 0;
    
    return {
      valorTotalBruto,
      valorTotalLiquido,
      valorMedio,
      totalNotas
    };
  };
  
  const resumoFinanceiroFiltrado = calcularResumoFinanceiroPorAno(anoResumoFinanceiro);
  
  // Calcular dados do dashboard filtrados por ano
  const dadosDashboardFiltrados = useMemo(() => {
    if (!notasBrutas || notasBrutas.length === 0 || !contratosBrutos || contratosBrutos.length === 0) {
      return dados;
    }
    
    return processarDados(notasBrutas, contratosBrutos, dados.avaliacoes || [], anoDashboardGeral);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notasBrutas, contratosBrutos, anoDashboardGeral, dados.avaliacoes]);

  // Função para calcular métricas do gráfico
  const calcularMetricas = () => {
    // Se temos totais do backend, usar eles (mais preciso)
    if (totaisGrafico) {
      // Calcular média mensal baseada nos meses que têm dados
      const mesesComDados = dadosGrafico ? dadosGrafico.filter(item => (parseFloat(item.totalLiquido) || 0) > 0).length : 0;
      const mesesComDadosCount = mesesComDados > 0 ? mesesComDados : 12; // Se não houver dados, usar 12 como padrão
      
      return {
        totalAbsolutoBruto: totaisGrafico.totalAbsolutoBruto || 0,
        totalAbsolutoLiquido: totaisGrafico.totalAbsolutoLiquido || 0,
        valorMedioAnualBruto: totaisGrafico.valorMedioAnualBruto || 0,
        valorMedioAnualLiquido: totaisGrafico.valorMedioAnualLiquido || 0,
        totalAnual: totaisGrafico.totalAbsolutoLiquido || 0,
        totalBrutoAnual: totaisGrafico.totalAbsolutoBruto || 0,
        mediaMensalLiquida: totaisGrafico.totalAbsolutoLiquido ? totaisGrafico.totalAbsolutoLiquido / mesesComDadosCount : 0,
        mediaMensalBruta: totaisGrafico.totalAbsolutoBruto ? totaisGrafico.totalAbsolutoBruto / mesesComDadosCount : 0,
        totalNotasAno: totaisGrafico.totalNotasAno || 0
      };
    }
    
    // Fallback: calcular localmente se não tiver totais do backend
    if (!dadosGrafico || dadosGrafico.length === 0) {
      return {
        totalAbsolutoBruto: 0,
        totalAbsolutoLiquido: 0,
        valorMedioAnualBruto: 0,
        valorMedioAnualLiquido: 0,
        totalAnual: 0,
        totalBrutoAnual: 0,
        mediaMensalLiquida: 0,
        mediaMensalBruta: 0,
        totalNotasAno: 0
      };
    }

    const totalAnual = dadosGrafico.reduce((sum, item) => sum + (parseFloat(item.totalLiquido) || 0), 0);
    const totalBrutoAnual = dadosGrafico.reduce((sum, item) => sum + (parseFloat(item.totalBruto) || 0), 0);
    const mesesComDados = dadosGrafico.filter(item => (parseFloat(item.totalLiquido) || 0) > 0).length || 1;
    const totalNotasAno = dadosGrafico.reduce((sum, item) => sum + (parseInt(item.totalNotas) || 0), 0);
    
    return {
      totalAbsolutoBruto: totalBrutoAnual,
      totalAbsolutoLiquido: totalAnual,
      valorMedioAnualBruto: totalNotasAno > 0 ? totalBrutoAnual / totalNotasAno : 0,
      valorMedioAnualLiquido: totalNotasAno > 0 ? totalAnual / totalNotasAno : 0,
      totalAnual,
      totalBrutoAnual,
      mediaMensalLiquida: totalAnual / mesesComDados,
      mediaMensalBruta: totalBrutoAnual / mesesComDados,
      totalNotasAno
    };
  };

  // Função para calcular média móvel (valor único horizontal)
  const calcularMediaMovel = () => {
    // Se o usuário definiu um valor customizado, usar esse valor
    if (mediaMovelCustomizada !== null) {
      return mediaMovelCustomizada;
    }
    
    if (!dadosGrafico || dadosGrafico.length === 0) return 0;
    
    // Calcular média de todos os meses com dados
    const mesesComDados = dadosGrafico.filter(item => (parseFloat(item.totalLiquido || item.total || 0)) > 0);
    if (mesesComDados.length === 0) return 0;
    
    const somaTotal = mesesComDados.reduce((sum, item) => {
      return sum + (parseFloat(item.totalLiquido || item.total || 0));
    }, 0);
    
    return somaTotal / mesesComDados.length;
  };

  // Função para calcular média móvel real (sem customização)
  const calcularMediaMovelReal = () => {
    if (!dadosGrafico || dadosGrafico.length === 0) return 0;
    
    // Calcular média de todos os meses com dados
    const mesesComDados = dadosGrafico.filter(item => (parseFloat(item.totalLiquido || item.total || 0)) > 0);
    if (mesesComDados.length === 0) return 0;
    
    const somaTotal = mesesComDados.reduce((sum, item) => {
      return sum + (parseFloat(item.totalLiquido || item.total || 0));
    }, 0);
    
    return somaTotal / mesesComDados.length;
  };

  // Função para abrir modal de configuração da média móvel
  const handleAbrirConfigMediaMovel = () => {
    // Mostrar o valor atual (customizado ou calculado) no input
    const valorAtual = calcularMediaMovel();
    setValorMediaMovelInput(valorAtual.toFixed(2).replace('.', ','));
    setModalMediaMovelOpen(true);
  };

  // Função para salvar valor customizado da média móvel
  const salvarMediaMovelCustomizada = () => {
    // Converter valor de string (com vírgula) para número
    const valor = parseFloat(valorMediaMovelInput.replace(',', '.'));
    
    if (isNaN(valor) || valor < 0) {
      alert('Por favor, insira um valor válido maior ou igual a zero.');
      return;
    }
    
    setMediaMovelCustomizada(valor);
    setModalMediaMovelOpen(false);
    setValorMediaMovelInput('');
  };

  // Função para resetar média móvel para valor calculado
  const resetarMediaMovel = () => {
    setMediaMovelCustomizada(null);
    setModalMediaMovelOpen(false);
    setValorMediaMovelInput('');
  };

  // Função para buscar análises críticas
  const fetchAnalisesCriticas = async () => {
    try {
      const anoAtual = new Date().getFullYear();
      const url = unidadeSelecionada
        ? `${BACKEND_URL}/api/analises-criticas?ano=${anoAtual}&unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/analises-criticas?ano=${anoAtual}`;
      
      const response = await apiFetch(url);
      if (response.ok) {
        const analises = await response.json();
        const analisesMap = {};
        analises.forEach(a => {
          analisesMap[`${a.mes}-${a.ano}`] = a.analise;
        });
        setAnalisesCriticas(analisesMap);
      }
    } catch (error) {
      console.error('Erro ao buscar análises críticas:', error);
    }
  };

  // Função para salvar análise crítica
  const salvarAnaliseCritica = async () => {
    if (!mesSelecionadoAnalise || !textoAnaliseCritica.trim()) {
      alert('Por favor, selecione um mês e preencha a análise crítica.');
      return;
    }

    try {
      const anoAtual = new Date().getFullYear();
      const response = await apiFetch(withUnidade('/api/analises-criticas', unidadeSelecionada), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mes: mesSelecionadoAnalise,
          ano: anoAtual.toString(),
          analise: textoAnaliseCritica.trim(),
          usuario: 'Usuario', // TODO: pegar do contexto de autenticação
          unidade: unidadeSelecionada?.codigo || null
        })
      });

      if (response.ok) {
        alert('Análise crítica salva com sucesso!');
        setModalAnaliseCriticaOpen(false);
        setTextoAnaliseCritica('');
        setMesSelecionadoAnalise('');
        fetchAnalisesCriticas();
      } else {
        throw new Error('Erro ao salvar análise crítica');
      }
    } catch (error) {
      console.error('Erro ao salvar análise crítica:', error);
      alert('Erro ao salvar análise crítica. Tente novamente.');
    }
  };

  // Função para abrir modal de análise crítica
  const handleAbrirAnaliseCritica = () => {
    setModalAnaliseCriticaOpen(true);
    const anoAtual = new Date().getFullYear();
    const mesAtual = new Date().getMonth() + 1;
    const mesFormatado = mesAtual.toString().padStart(2, '0');
    const chave = `${mesFormatado}-${anoAtual}`;
    if (analisesCriticas[chave]) {
      setTextoAnaliseCritica(analisesCriticas[chave]);
      setMesSelecionadoAnalise(mesFormatado);
    }
  };

  // Função para processar dados do gráfico (não utilizada - mantida para referência futura)
  // eslint-disable-next-line no-unused-vars
  const processarDadosGrafico = () => {
    console.log('🔍 Processando dados do gráfico:', dadosGrafico);
    
    if (!dadosGrafico || !Array.isArray(dadosGrafico) || dadosGrafico.length === 0) {
      console.log('⚠️ Dados inválidos ou vazios');
      return {
        labels: [],
        datasets: []
      };
    }

    try {
      const labels = dadosGrafico.map(item => {
        if (typeof item === 'object' && item && item.nome) {
          return String(item.nome);
        }
        return 'Mês';
      });
      
      const valores = dadosGrafico.map(item => {
        if (typeof item === 'object' && item && item.valor !== undefined) {
          return parseFloat(item.valor) || 0;
        }
        return 0;
      });

      const dadosProcessados = {
        labels,
        datasets: [
          {
            label: 'Valor Líquido (R$)',
            data: valores,
            backgroundColor: [
              'rgba(76, 175, 80, 0.8)',
              'rgba(33, 150, 243, 0.8)',
              'rgba(255, 152, 0, 0.8)',
              'rgba(156, 39, 176, 0.8)',
              'rgba(244, 67, 54, 0.8)',
              'rgba(0, 150, 136, 0.8)',
              'rgba(255, 193, 7, 0.8)',
              'rgba(63, 81, 181, 0.8)',
              'rgba(233, 30, 99, 0.8)',
              'rgba(121, 85, 72, 0.8)',
              'rgba(96, 125, 139, 0.8)',
              'rgba(139, 195, 74, 0.8)'
            ],
            borderColor: [
              'rgba(76, 175, 80, 1)',
              'rgba(33, 150, 243, 1)',
              'rgba(255, 152, 0, 1)',
              'rgba(156, 39, 176, 1)',
              'rgba(244, 67, 54, 1)',
              'rgba(0, 150, 136, 1)',
              'rgba(255, 193, 7, 1)',
              'rgba(63, 81, 181, 1)',
              'rgba(233, 30, 99, 1)',
              'rgba(121, 85, 72, 1)',
              'rgba(96, 125, 139, 1)',
              'rgba(139, 195, 74, 1)'
            ],
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
          }
        ]
      };

      console.log('✅ Dados processados:', dadosProcessados);
      return dadosProcessados;
    } catch (error) {
      console.error('❌ Erro ao processar dados:', error);
      return {
        labels: [],
        datasets: []
      };
    }
  };

  // Função para renderizar dados do gráfico
  const renderizarDadosGrafico = () => {
    if (!dadosGrafico || !Array.isArray(dadosGrafico) || dadosGrafico.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column'
        }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            📊 Nenhum dado encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Não há dados de gastos mensais para exibir
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dados recebidos: {JSON.stringify(dadosGrafico)}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '100%',
        flexDirection: 'column'
      }}>
        <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
          📊 Gráfico de Gastos Mensais
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1,
          width: '100%',
          maxHeight: '300px',
          overflow: 'auto'
        }}>
          {dadosGrafico.map((item, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '4px',
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}>
              <Typography variant="body1" fontWeight="bold">
                {item.nome}
              </Typography>
              <Typography variant="body1" color="primary" fontWeight="bold">
                R$ {parseFloat(item.valor || 0).toLocaleString('pt-BR', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Configurações do gráfico (não utilizada - mantida para referência futura)
  // eslint-disable-next-line no-unused-vars
  const opcoesGrafico = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 14,
            weight: 'bold'
          }
        }
      },
      title: {
        display: true,
        text: 'Gastos Mensais por Valor Líquido',
        font: {
          size: 18,
          weight: 'bold'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const valor = context.parsed.y;
            return `Valor: R$ ${valor.toLocaleString('pt-BR', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return 'R$ ' + value.toLocaleString('pt-BR');
          }
        },
        grid: {
          color: 'rgba(0,0,0,0.1)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);
  
  // Atualizar resumo financeiro quando o ano mudar
  useEffect(() => {
    if (notasBrutas.length > 0) {
      // O resumo já é calculado dinamicamente via calcularResumoFinanceiroPorAno
      // Não precisa fazer nada aqui, apenas garantir que o estado está atualizado
    }
  }, [anoResumoFinanceiro, notasBrutas]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Carregando Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>
      <VisaoRestritaBanner />
      {/* Header Moderno */}
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px 32px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        border: '1px solid rgba(255,255,255,0.3)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <BarChart sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 700
            }}>
              Dashboard Financeiro
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Análise completa de processos e valores financeiros
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={anoDashboardGeral}
              onChange={(e) => setAnoDashboardGeral(e.target.value)}
              sx={{
                backgroundColor: 'white',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#667eea',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#764ba2',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#764ba2',
                },
              }}
            >
              <MenuItem value="2025">2025</MenuItem>
              <MenuItem value="2026">2026</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<BarChart />}
            onClick={handleAbrirGrafico}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
              boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                boxShadow: '0 6px 25px rgba(76, 175, 80, 0.5)',
                transform: 'translateY(-2px)',
              },
              minWidth: '160px',
              height: '48px'
            }}
          >
            📊 GRÁFICO
          </Button>
        </Box>
      </Box>

      {/* Resumo Geral - Design Moderno */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(33, 150, 243, 0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(33, 150, 243, 0.2)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box sx={{
                  p: 1,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  mr: 1.5
                }}>
                  <Assessment color="primary" />
                </Box>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                  Total de Processos
                </Typography>
              </Box>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                {dadosDashboardFiltrados.resumoGeral.totalProcessos}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(76, 175, 80, 0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(76, 175, 80, 0.2)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box sx={{
                  p: 1,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  mr: 1.5
                }}>
                  <CheckCircle color="success" />
                </Box>
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                  Processos Pagos
                </Typography>
              </Box>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                {dadosDashboardFiltrados.resumoGeral.processosPagos}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 152, 0, 0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(255, 152, 0, 0.2)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box sx={{
                  p: 1,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  mr: 1.5
                }}>
                  <Pending color="warning" />
                </Box>
                <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                  Processos Pendentes
                </Typography>
              </Box>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                {dadosDashboardFiltrados.resumoGeral.processosPendentes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(244, 67, 54, 0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(244, 67, 54, 0.2)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box sx={{
                  p: 1,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  mr: 1.5
                }}>
                  <Warning color="error" />
                </Box>
                <Typography variant="h6" color="error.main" sx={{ fontWeight: 600 }}>
                  Processos em Atraso
                </Typography>
              </Box>
              <Typography variant="h3" color="error.main" sx={{ fontWeight: 700 }}>
                {dadosDashboardFiltrados.resumoGeral.processosAtraso}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Resumo Financeiro */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #f8fafc 0%, #e3f2fd 50%, #f8fafc 100%)', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChart color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                Resumo Financeiro
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={anoResumoFinanceiro}
                onChange={(e) => setAnoResumoFinanceiro(e.target.value)}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2196f3',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                }}
              >
                <MenuItem value="2025">2025</MenuItem>
                <MenuItem value="2026">2026</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                backgroundColor: '#e3f2fd', 
                border: '1px solid #2196f3',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(33,150,243,0.2)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(33,150,243,0.3)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: '8px', 
                      backgroundColor: 'rgba(33,150,243,0.1)',
                      mr: 1.5
                    }}>
                      <AttachMoney color="primary" />
                    </Box>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                      Valor Bruto Total
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                    R$ {resumoFinanceiroFiltrado.valorTotalBruto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                backgroundColor: '#e8f5e8', 
                border: '1px solid #4caf50',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(76,175,80,0.2)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(76,175,80,0.3)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: '8px', 
                      backgroundColor: 'rgba(76,175,80,0.1)',
                      mr: 1.5
                    }}>
                      <TrendingUp color="success" />
                    </Box>
                    <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                      Valor Líquido Total
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                    R$ {resumoFinanceiroFiltrado.valorTotalLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                backgroundColor: '#fff3e0', 
                border: '1px solid #ff9800',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(255,152,0,0.2)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(255,152,0,0.3)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: '8px', 
                      backgroundColor: 'rgba(255,152,0,0.1)',
                      mr: 1.5
                    }}>
                      <TrendingDown color="warning" />
                    </Box>
                    <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                      Valor Médio
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                    R$ {resumoFinanceiroFiltrado.valorMedio.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    por nota fiscal
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Evolução – últimos 6 meses (notas fiscais e solicitações) */}
      <Card sx={{ mb: 4, borderRadius: '20px', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: '1px solid rgba(255,255,255,0.3)' }}>
        <CardContent sx={{ p: 3 }}>
          {loadingEvolucaoNotas ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
              <CircularProgress />
            </Box>
          ) : (
            <EvolucaoNotasFiscaisChart
                dados={evolucaoNotas}
                loading={false}
                ano={anoGraficoNotas}
                onAnoChange={(v) => setAnoGraficoNotas(v)}
              />
          )}
        </CardContent>
      </Card>

      {/* Modal de Gráfico - Design Moderno */}
      <Dialog
        open={modalGraficoOpen}
        onClose={() => setModalGraficoOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.3)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '24px 24px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 32px',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1,
              borderRadius: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BarChart sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h5" fontWeight={700}>
              Gráfico de Gastos Mensais
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setModalGraficoOpen(false)}
            sx={{ 
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                transform: 'rotate(90deg)',
                transition: 'all 0.3s ease'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)' }}>
          {loadingGrafico ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              py: 8,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} sx={{ color: '#667eea' }} />
              <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 600 }}>
                Carregando dados do gráfico...
              </Typography>
            </Box>
          ) : (
            <Card sx={{ 
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pb: 2,
                  mb: 3,
                  borderBottom: '2px solid rgba(102, 126, 234, 0.1)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      p: 1,
                      borderRadius: '10px',
                      backgroundColor: 'rgba(102, 126, 234, 0.1)'
                    }}>
                      <Assessment sx={{ color: '#667eea', fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" sx={{ 
                      color: '#667eea', 
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      Análise por Tipo de Serviço
                    </Typography>
                  </Box>
                  
                  {/* Botões de controle modernos */}
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={anoGrafico}
                        onChange={(e) => {
                          const ano = e.target.value;
                          setAnoGrafico(ano);
                          if (notasBrutas && notasBrutas.length > 0) {
                            const { meses, totais } = computeDadosGraficoFromNotas(ano);
                            setDadosGrafico(meses);
                            setTotaisGrafico(totais);
                            setLoadingGrafico(false);
                          } else {
                            fetchDadosMensais(ano);
                          }
                        }}
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: '10px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#764ba2',
                          },
                        }}
                      >
                        <MenuItem value="2025">2025</MenuItem>
                        <MenuItem value="2026">2026</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={mostrarMediaMovel}
                          onChange={(e) => setMostrarMediaMovel(e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#667eea',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#667eea',
                            },
                          }}
                        />
                      }
                      label="Média Móvel"
                      sx={{ color: '#667eea', fontWeight: 500 }}
                    />
                    {mostrarMediaMovel && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAbrirConfigMediaMovel}
                        sx={{ 
                          minWidth: '140px',
                          borderRadius: '10px',
                          borderColor: '#667eea',
                          color: '#667eea',
                          '&:hover': {
                            borderColor: '#764ba2',
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          }
                        }}
                      >
                        Configurar Valor
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAbrirAnaliseCritica}
                      sx={{ 
                        minWidth: '150px',
                        borderRadius: '10px',
                        borderColor: '#f5576c',
                        color: '#f5576c',
                        '&:hover': {
                          borderColor: '#f093fb',
                          backgroundColor: 'rgba(245, 87, 108, 0.1)',
                        }
                      }}
                    >
                      Análise Crítica
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAbrirGraficoPrestador}
                      startIcon={<Business />}
                      sx={{ 
                        minWidth: '180px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                          boxShadow: '0 6px 25px rgba(102, 126, 234, 0.5)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Gráfico por Prestador
                    </Button>
                  </Box>
                </Box>
                
                {dadosGrafico && dadosGrafico.length > 0 ? (
                  <Box sx={{ 
                    height: '600px', 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                        {/* Cabeçalho com métricas */}
                        {(() => {
                          const metricas = calcularMetricas();
                          return (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: 2,
                          p: 2,
                          backgroundColor: '#f5f5f5',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                            📊 Análise Mensal de Gastos - Ano {anoGrafico}
                          </Typography>
                          
                          {/* Primeira linha: Valores Totais Absolutos */}
                          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                              <Typography variant="caption" color="text.secondary" display="block">Valor Total Absoluto Bruto</Typography>
                              <Typography variant="h6" color="warning.main" sx={{ fontWeight: 'bold' }}>
                                R$ {metricas.totalAbsolutoBruto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                              <Typography variant="caption" color="text.secondary" display="block">Valor Total Absoluto Líquido</Typography>
                              <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                                R$ {metricas.totalAbsolutoLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                              <Typography variant="caption" color="text.secondary" display="block">Valor Médio Mensal Bruto</Typography>
                              <Typography variant="h6" color="info.main" sx={{ fontWeight: 'bold' }}>
                                R$ {metricas.mediaMensalBruta.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                              <Typography variant="caption" color="text.secondary" display="block">Valor Médio Mensal Líquido</Typography>
                              <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                R$ {metricas.mediaMensalLiquida.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                              <Typography variant="caption" color="text.secondary" display="block">Valor Médio Anual Bruto</Typography>
                              <Typography variant="h6" color="warning.dark" sx={{ fontWeight: 'bold' }}>
                                R$ {metricas.valorMedioAnualBruto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                              <Typography variant="caption" color="text.secondary" display="block">Valor Médio Anual Líquido</Typography>
                              <Typography variant="h6" color="success.dark" sx={{ fontWeight: 'bold' }}>
                                R$ {metricas.valorMedioAnualLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                            {mostrarMediaMovel && (() => {
                              const mediaMovel = calcularMediaMovel();
                              return (
                                <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {mediaMovelCustomizada !== null ? 'Média Móvel (Customizada)' : 'Média Móvel (Calculada)'}
                                  </Typography>
                                  <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold' }}>
                                    R$ {mediaMovel.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </Box>
                        </Box>
                          );
                        })()}

                        {/* Container do gráfico Chart.js */}
                        <Box sx={{ 
                          width: '100%',
                          height: '500px',
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          border: '2px solid #e0e0e0',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          p: 2
                        }}>
                        {/* Gráfico Chart.js com barras empilhadas */}
                            {(() => {
                          // Preparar dados para Chart.js
                          const labels = dadosGrafico.map(item => {
                            // Formato: jan/25, fev/25, etc. ou jan/26, fev/26, etc.
                            const mesesAbrev = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
                            const mesIndex = parseInt(item.mes) - 1;
                            const anoAbrev = anoGrafico?.slice(-2) || '25';
                            return mesIndex >= 0 && mesIndex < 12 ? `${mesesAbrev[mesIndex]}/${anoAbrev}` : item.nome;
                          });

                          const dadosTerceirizada = dadosGrafico.map(item => parseFloat(item.terceirizada || 0));
                          const dadosEventual = dadosGrafico.map(item => parseFloat(item.eventual || 0));
                          const dadosCorporativo = dadosGrafico.map(item => parseFloat(item.corporativo || 0));
                          const dadosMedicos = dadosGrafico.map(item => parseFloat(item.medicos || 0));
                          const dadosSemTipo = dadosGrafico.map(item => {
                            const total = parseFloat(item.totalLiquido || item.total || 0);
                        const terceirizada = parseFloat(item.terceirizada || 0);
                        const eventual = parseFloat(item.eventual || 0);
                        const corporativo = parseFloat(item.corporativo || 0);
                        const medicos = parseFloat(item.medicos || 0);
                            return total - (terceirizada + eventual + corporativo + medicos);
                          });

                          const mediaMovel = calcularMediaMovel();
                          const valoresGrafico = dadosGrafico.map(d => parseFloat(d.totalLiquido || d.total || 0));
                          // eslint-disable-next-line no-unused-vars
                          const maxValor = Math.max(
                            ...valoresGrafico,
                            mostrarMediaMovel ? mediaMovel : 0
                          );

                          const chartData = {
                            labels,
                            datasets: [
                              {
                                label: 'Terceirizada',
                                data: dadosTerceirizada,
                                backgroundColor: '#4caf50',
                                borderColor: '#4caf50',
                                borderWidth: 0,
                              },
                              {
                                label: 'Eventual',
                                data: dadosEventual,
                                backgroundColor: '#ff9800',
                                borderColor: '#ff9800',
                                borderWidth: 0,
                              },
                              {
                                label: 'Corporativo',
                                data: dadosCorporativo,
                                backgroundColor: '#2196f3',
                                borderColor: '#2196f3',
                                borderWidth: 0,
                              },
                              {
                                label: 'Médicos',
                                data: dadosMedicos,
                                      backgroundColor: '#9c27b0',
                                borderColor: '#9c27b0',
                                borderWidth: 0,
                              },
                              {
                                label: 'Sem Tipo',
                                data: dadosSemTipo,
                                backgroundColor: '#9e9e9e',
                                borderColor: '#9e9e9e',
                                borderWidth: 0,
                              },
                              // Linha de média móvel horizontal
                              ...(mostrarMediaMovel ? [{
                                label: 'Média Móvel',
                                data: Array(labels.length).fill(mediaMovel),
                                type: 'line',
                                borderColor: '#f44336',
                                backgroundColor: 'transparent',
                                borderWidth: 3,
                                borderDash: [],
                                pointRadius: 0,
                                pointHoverRadius: 0,
                                fill: false,
                                tension: 0,
                                order: -1, // Desenhar por último (acima das barras)
                                yAxisID: 'y',
                              }] : [])
                            ]
                          };

                          const chartOptions = {
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              valorLiquidoLabels: {
                                valorLiquidoPorBarra: dadosGrafico.map(d => parseFloat(d.totalLiquido || d.total || 0))
                              },
                              legend: {
                                display: true,
                                position: 'bottom',
                                labels: {
                                  usePointStyle: true,
                                  padding: 15,
                                  font: {
                                    size: 12,
                                    weight: 'bold'
                                  }
                                }
                              },
                              tooltip: {
                                callbacks: {
                                  label: function(context) {
                                    const label = context.dataset.label || '';
                                    const value = context.parsed.y || 0;
                                    if (label === 'Média Móvel') {
                                      return `Média Móvel: R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                    }
                                    return `${label}: R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                  }
                                }
                              }
                            },
                            scales: {
                              x: {
                                stacked: true,
                                grid: {
                                  display: false
                                },
                                ticks: {
                                  font: {
                                    size: 11,
                                    weight: 'bold'
                                  }
                                }
                              },
                              y: {
                                stacked: true,
                                beginAtZero: true,
                                ticks: {
                                  callback: function(value) {
                                    return 'R$ ' + (value / 1000).toFixed(0) + 'k';
                                  },
                                  font: {
                                    size: 11
                                  }
                                },
                                grid: {
                                  color: 'rgba(0,0,0,0.1)',
                                  drawBorder: false
                                }
                              }
                            }
                          };

                          return (
                            <Bar data={chartData} options={chartOptions} />
                          );
                        })()}
                        </Box>
                    
                        {/* Legenda */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 20, height: 20, backgroundColor: '#4caf50', borderRadius: '4px' }} />
                            <Typography variant="body2">Terceirizada</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 20, height: 20, backgroundColor: '#ff9800', borderRadius: '4px' }} />
                            <Typography variant="body2">Eventual</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 20, height: 20, backgroundColor: '#2196f3', borderRadius: '4px' }} />
                            <Typography variant="body2">Corporativo</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 20, height: 20, backgroundColor: '#9c27b0', borderRadius: '4px' }} />
                            <Typography variant="body2">Médicos</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 20, height: 20, backgroundColor: '#9e9e9e', borderRadius: '4px' }} />
                            <Typography variant="body2">Sem Tipo</Typography>
                          </Box>
                        </Box>
                        
                        {/* Nota sobre escala */}
                        <Box sx={{ 
                          mt: 2, 
                          p: 2, 
                          backgroundColor: '#f5f5f5', 
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                            📊 <strong>Nota:</strong> Valores com ⚠️ indicam outliers extremos. 
                            A escala foi ajustada para melhor visualização dos dados.
                          </Typography>
                        </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '300px',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                      📊 Nenhum dado encontrado
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Não há dados de gastos mensais para o ano {anoGrafico || 'selecionado'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Verifique se existem notas fiscais cadastradas para este ano.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          background: 'rgba(255, 255, 255, 0.5)',
          borderTop: '1px solid rgba(102, 126, 234, 0.1)'
        }}>
          <Button 
            onClick={() => setModalGraficoOpen(false)}
            variant="outlined"
            sx={{ 
              borderRadius: '12px',
              padding: '10px 24px',
              borderColor: '#667eea',
              color: '#667eea',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#764ba2',
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Configuração da Média Móvel */}
      <Dialog
        open={modalMediaMovelOpen}
        onClose={() => {
          setModalMediaMovelOpen(false);
          setValorMediaMovelInput('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h5" fontWeight={600}>
            ⚙️ Configurar Média Móvel
          </Typography>
          <IconButton 
            onClick={() => {
              setModalMediaMovelOpen(false);
              setValorMediaMovelInput('');
            }}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Valor atual calculado automaticamente:
              </Typography>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
                R$ {calcularMediaMovelReal().toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>

            <TextField
              label="Valor da Média Móvel (R$)"
              fullWidth
              value={valorMediaMovelInput}
              onChange={(e) => {
                // Permitir apenas números, vírgula e ponto
                const valor = e.target.value.replace(/[^\d,.-]/g, '');
                setValorMediaMovelInput(valor);
              }}
              placeholder="Ex: 980000,00 ou 980000.00"
              variant="outlined"
              helperText="Digite o valor desejado para a linha de média móvel. Use vírgula ou ponto como separador decimal."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: 'text.secondary', fontWeight: 'bold' }}>R$</Typography>
                  </InputAdornment>
                )
              }}
            />

            {mediaMovelCustomizada !== null && (
              <Alert severity="info">
                Você está usando um valor customizado. Clique em "Usar Valor Calculado" para voltar ao valor automático.
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setModalMediaMovelOpen(false);
              setValorMediaMovelInput('');
            }}
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          >
            Cancelar
          </Button>
          {mediaMovelCustomizada !== null && (
            <Button 
              onClick={resetarMediaMovel}
              variant="outlined"
              color="secondary"
              sx={{ borderRadius: '8px' }}
            >
              Usar Valor Calculado
            </Button>
          )}
          <Button 
            onClick={salvarMediaMovelCustomizada}
            variant="contained"
            color="error"
            sx={{ borderRadius: '8px' }}
            disabled={!valorMediaMovelInput.trim()}
          >
            Salvar Valor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Análise Crítica */}
      <Dialog
        open={modalAnaliseCriticaOpen}
        onClose={() => {
          setModalAnaliseCriticaOpen(false);
          setTextoAnaliseCritica('');
          setMesSelecionadoAnalise('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h5" fontWeight={600}>
            📝 Análise Crítica Mensal
          </Typography>
          <IconButton 
            onClick={() => {
              setModalAnaliseCriticaOpen(false);
              setTextoAnaliseCritica('');
              setMesSelecionadoAnalise('');
            }}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Selecione o Mês</InputLabel>
              <Select
                value={mesSelecionadoAnalise}
                onChange={(e) => {
                  setMesSelecionadoAnalise(e.target.value);
                  const anoAtual = new Date().getFullYear();
                  const chave = `${e.target.value}-${anoAtual}`;
                  if (analisesCriticas[chave]) {
                    setTextoAnaliseCritica(analisesCriticas[chave]);
                  } else {
                    setTextoAnaliseCritica('');
                  }
                }}
                label="Selecione o Mês"
              >
                {dadosGrafico.map((item, index) => (
                  <MenuItem key={index} value={item.mes}>
                    {item.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Análise Crítica"
              multiline
              rows={8}
              fullWidth
              value={textoAnaliseCritica}
              onChange={(e) => setTextoAnaliseCritica(e.target.value)}
              placeholder="Descreva o que ocorreu neste mês, fatores que influenciaram os gastos, eventos relevantes, etc."
              variant="outlined"
            />

            {mesSelecionadoAnalise && analisesCriticas[`${mesSelecionadoAnalise}-${new Date().getFullYear()}`] && (
              <Alert severity="info">
                Já existe uma análise crítica para este mês. Ao salvar, ela será atualizada.
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setModalAnaliseCriticaOpen(false);
              setTextoAnaliseCritica('');
              setMesSelecionadoAnalise('');
            }}
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={salvarAnaliseCritica}
            variant="contained"
            color="secondary"
            sx={{ borderRadius: '8px' }}
            disabled={!mesSelecionadoAnalise || !textoAnaliseCritica.trim()}
          >
            Salvar Análise
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Gráfico por Prestador */}
      <Dialog
        open={modalGraficoPrestadorOpen}
        onClose={() => setModalGraficoPrestadorOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h5" fontWeight={600}>
            📊 Gráfico de Valores Mensais por Prestador
          </Typography>
          <IconButton 
            onClick={() => setModalGraficoPrestadorOpen(false)}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Seleção de Prestador e Ano */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl fullWidth sx={{ maxWidth: 400 }}>
                <InputLabel>Selecione o Prestador</InputLabel>
                <Select
                  value={prestadorSelecionadoGrafico}
                  onChange={(e) => {
                    setPrestadorSelecionadoGrafico(e.target.value);
                    if (e.target.value && anoGraficoPrestador) {
                      fetchDadosMensaisPrestador(e.target.value, anoGraficoPrestador);
                    }
                  }}
                  label="Selecione o Prestador"
                >
                  {contratosBrutos.map((contrato) => (
                    <MenuItem key={contrato.id} value={contrato.numeroContrato}>
                      {contrato.empresa} - {contrato.numeroContrato}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Ano</InputLabel>
                <Select
                  value={anoGraficoPrestador}
                  onChange={(e) => {
                    setAnoGraficoPrestador(e.target.value);
                    if (prestadorSelecionadoGrafico && e.target.value) {
                      fetchDadosMensaisPrestador(prestadorSelecionadoGrafico, e.target.value);
                    }
                  }}
                  label="Ano"
                >
                  <MenuItem value="2025">2025</MenuItem>
                  <MenuItem value="2026">2026</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {loadingGraficoPrestador ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ ml: 2 }}>
                  Carregando dados do gráfico...
                </Typography>
              </Box>
            ) : dadosGraficoPrestador && dadosGraficoPrestador.length > 0 ? (
              <Card elevation={3} sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ 
                    height: '500px', 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                    {/* Métricas */}
                    {(() => {
                      const totalAnual = dadosGraficoPrestador.reduce((sum, item) => sum + item.valorLiquido, 0);
                      const mediaMensal = dadosGraficoPrestador.length > 0 ? totalAnual / dadosGraficoPrestador.length : 0;
                      const totalNotas = dadosGraficoPrestador.reduce((sum, item) => sum + item.quantidade, 0);
                      
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2,
                          backgroundColor: '#f5f5f5',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Total Anual</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              R$ {totalAnual.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Média Mensal</Typography>
                            <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                              R$ {mediaMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Total de Notas</Typography>
                            <Typography variant="h6" color="warning.main" sx={{ fontWeight: 'bold' }}>
                              {totalNotas}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })()}

                    {/* Gráfico */}
                    <Box sx={{ 
                      width: '100%',
                      height: '400px',
                      backgroundColor: '#ffffff',
                      borderRadius: '12px',
                      border: '2px solid #e0e0e0',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      p: 2
                    }}>
                      {(() => {
                        const mesesAbrev = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
                        const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
                        
                        const labels = dadosGraficoPrestador.map(item => {
                          const mesIndex = meses.indexOf(item.mes);
                          const anoAbrev = anoGraficoPrestador?.slice(-2) || '25';
                          return mesIndex >= 0 ? `${mesesAbrev[mesIndex]}/${anoAbrev}` : item.mes;
                        });
                        
                        const valores = dadosGraficoPrestador.map(item => item.valorLiquido);

                        const chartData = {
                          labels,
                          datasets: [
                            {
                              label: 'Valor Líquido Mensal (R$)',
                              data: valores,
                              backgroundColor: 'rgba(33, 150, 243, 0.8)',
                              borderColor: 'rgba(33, 150, 243, 1)',
                              borderWidth: 2,
                              borderRadius: 8,
                              borderSkipped: false,
                            }
                          ]
                        };

                        const chartOptions = {
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top',
                              labels: {
                                usePointStyle: true,
                                padding: 15,
                                font: {
                                  size: 12,
                                  weight: 'bold'
                                }
                              }
                            },
                            title: {
                              display: true,
                              text: `Valores Mensais - ${contratosBrutos.find(c => c.numeroContrato === prestadorSelecionadoGrafico)?.empresa || 'Prestador'} (${anoGraficoPrestador})`,
                              font: {
                                size: 16,
                                weight: 'bold'
                              }
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const value = context.parsed.y || 0;
                                  return `Valor: R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                }
                              }
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return 'R$ ' + (value / 1000).toFixed(0) + 'k';
                                },
                                font: {
                                  size: 11
                                }
                              },
                              grid: {
                                color: 'rgba(0,0,0,0.1)',
                                drawBorder: false
                              }
                            },
                            x: {
                              grid: {
                                display: false
                              },
                              ticks: {
                                font: {
                                  size: 11,
                                  weight: 'bold'
                                }
                              }
                            }
                          }
                        };

                        return (
                          <Bar data={chartData} options={chartOptions} />
                        );
                      })()}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : prestadorSelecionadoGrafico ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '300px',
                flexDirection: 'column'
              }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  📊 Nenhum dado encontrado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Não há notas fiscais para este prestador no ano selecionado.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '300px',
                flexDirection: 'column'
              }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  📊 Selecione um Prestador
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecione um prestador e um ano para visualizar o gráfico de valores mensais.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setModalGraficoPrestadorOpen(false)}
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
